import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@unified-checkout/design-system';
import { BrowserBar } from './BrowserBar';
import { ViewModeToggle } from './ViewModeToggle';
import { getCheckoutUrl } from '@app/dashboard/utils/serverUrls';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { useLangStore } from '@app/dashboard/store/lang';
import { userInfo } from '@app/dashboard/utils/auth/userInfo';
import { useIntegrationsStore } from '@app/dashboard/store/integrations';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import {
    createPaymentMethodObject,
    categorizePaymentMethod,
} from '@app/dashboard/components/inner_layout/PayMeCard/PayMeCheckoutPreviewModal/utils';
import { normalizeGatewayName } from '@app/dashboard/constants/payment-method-types';
import type {
    IPayMePreviewSettings,
    ICheckoutPreviewPayload,
    ICheckoutPreviewMessage,
    PayMeCheckoutPreviewModalProps
} from '@app/dashboard/types/innerLayout/payme';
import { PAYME_BASE_URL } from '@app/dashboard/constants/payme-links';
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import styles from './payme-checkout-preview-modal.module.scss';

export const PayMeCheckoutPreviewModal = ({
    open,
    onClose,
    handle,
    paymentHandleConfig,
    checkoutCustomizationConfig,
}: PayMeCheckoutPreviewModalProps) => {
    const { t } = useTranslation();
    const { lang } = useLangStore();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [previewMood, setPreviewMood] = useState<'desktop' | 'mobile'>('desktop');

    const checkoutOrigin = getCheckoutUrl();
    const checkoutUrl = `${checkoutOrigin}/?previewMode=true`;
    const country = getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY);
    const countryCode = COUNTRIES_INFO[country]?.country_code_iso2;
    const countryCurrency = COUNTRIES_INFO[country]?.currency;

    // Get business information from userInfo and local storage
    const merchantInfo = userInfo();
    const merchantPhone = getFromLocalStorage(LOCAL_STORAGE_KEYS.PHONE) || '';
    const businessName = merchantInfo.companyName || getFromLocalStorage(LOCAL_STORAGE_KEYS.COMPANY_NAME) || '';
    const merchantLogo = merchantInfo.logo || getFromLocalStorage(LOCAL_STORAGE_KEYS.LOGO_URL) || null;

    const { paymentMethodIntegrations } = useIntegrationsStore();
    const { isLive } = useTestLiveModeStore();
    const currentMode = isLive ? 'live' : 'test';

    // Match payment methods from checkout-customization against payment handle config
    const paymentMethods = useMemo(() => {
        if (!paymentHandleConfig?.payment_methods_list) {
            return { methods: [], bnpl: [], x_pay: [] };
        }

        const selectedIntegrationIds = new Set(paymentHandleConfig.payment_methods_list);
        const foundByGatewayName = new Set<string>(); // Track methods found by gateway+name to avoid duplicates
        const methods: any[] = [];
        const bnpl: any[] = [];
        const x_pay: any[] = [];

        // Normalize a name by removing all separators for comparison
        const normalizeNameForComparison = (name: string): string => {
            return name.toLowerCase().replace(/[_-]/g, '');
        };

        // Find payment method in customization by gateway + name
        const findInCustomization = (gateway: string, gatewayType: string) => {
            const customizationPayments = checkoutCustomizationConfig?.customization_data?.payments;
            if (!customizationPayments) return null;
            
            const allMethods = [
                ...(customizationPayments.methods || []),
                ...(customizationPayments.bnpl || []),
                ...(customizationPayments.x_pay || []),
            ];
            
            // Try multiple name variations from gateway type
            const gatewayTypeLower = gatewayType.toLowerCase();
            const nameVariations = [
                gatewayTypeLower,                    // e.g., "aman_v3"
                gatewayTypeLower.replace(/_/g, '-'), // e.g., "aman-v3"
                gatewayTypeLower.replace(/_/g, ''),  // e.g., "amanv3"
            ];
            
            // First, filter by gateway
            const methodsWithGateway = allMethods.filter(
                (method) => method.gateway === gateway
            );
            
            // Try exact name match first
            for (const nameVariation of nameVariations) {
                const match = methodsWithGateway.find(
                    (method) => method.name === nameVariation
                );
                if (match) {
                    return match;
                }
            }
            
            // If no exact match, try normalized comparison (ignoring separators)
            const normalizedGatewayType = normalizeNameForComparison(gatewayType);
            const match = methodsWithGateway.find(
                (method) => normalizeNameForComparison(method.name) === normalizedGatewayType
            );
            
            return match || null;
        };

        if (!paymentMethodIntegrations) {
            return { methods, bnpl, x_pay };
        }

        const currency = paymentHandleConfig.currency || countryCurrency;
        const modeData = paymentMethodIntegrations[currentMode];
        
        if (!modeData || !modeData[currency]) {
            return { methods, bnpl, x_pay };
        }

        const currencyData = modeData[currency];

        // For each selected integration ID, find the gateway type and match with checkout-customization
        selectedIntegrationIds.forEach((integrationId: number) => {
            for (const gatewayType of Object.keys(currencyData)) {
                const integrations = currencyData[gatewayType];
                const matchingIntegration = integrations.find(
                    (integration) => integration.id === integrationId
                );
                
                if (matchingIntegration) {
                    const normalizedGateway = normalizeGatewayName(gatewayType);
                    const gatewayNameKey = `${normalizedGateway}:${gatewayType}`;
                    
                    // Check if this payment method exists in checkout-customization by gateway+name variations
                    const existingMethod = findInCustomization(normalizedGateway, gatewayType);
                    
                    if (existingMethod && !foundByGatewayName.has(gatewayNameKey)) {
                        // Use the method from checkout-customization as-is
                        const category = categorizePaymentMethod(
                            { gateway: normalizedGateway, name: existingMethod.name } as any,
                            gatewayType,
                            normalizedGateway
                        );
                        
                        if (category === 'x_pay') {
                            x_pay.push(existingMethod);
                        } else if (category === 'bnpl') {
                            bnpl.push(existingMethod);
                        } else {
                            methods.push(existingMethod);
                        }
                        
                        foundByGatewayName.add(gatewayNameKey);
                    } else if (!foundByGatewayName.has(gatewayNameKey)) {
                        // Not found in checkout-customization, build from integration data
                        const paymentMethodObject = createPaymentMethodObject(
                            gatewayType,
                            integrationId
                        );
                        
                        const category = categorizePaymentMethod(
                            paymentMethodObject,
                            gatewayType,
                            paymentMethodObject.gateway
                        );
                        
                        if (category === 'x_pay') {
                            x_pay.push(paymentMethodObject);
                        } else if (category === 'bnpl') {
                            bnpl.push(paymentMethodObject);
                        } else {
                            methods.push(paymentMethodObject);
                        }
                        
                        foundByGatewayName.add(gatewayNameKey);
                    }
                    break;
                }
            }
        });

        return { methods, bnpl, x_pay };
    }, [paymentHandleConfig, checkoutCustomizationConfig, paymentMethodIntegrations, currentMode, countryCurrency]);

    const getPayMeLinkUrl = () => {
        return `${PAYME_BASE_URL}/${handle}`;
    };

    const postMessageToCheckout = useCallback(() => {
        if (!iframeRef.current?.contentWindow || !paymentHandleConfig) return;

        const paymeSettings: IPayMePreviewSettings = {
            needName: paymentHandleConfig.is_collect_customer_name || false,
            needEmail: paymentHandleConfig.is_email_mandatory || false,
            needPhone: paymentHandleConfig.is_phone_mandatory || false,
            needPurpose: paymentHandleConfig.is_collect_payment_purpose || false,
        };

        // Use customization config if available, otherwise use defaults
        const customizationData = checkoutCustomizationConfig?.customization_data;
        const customMerchantLogo = checkoutCustomizationConfig?.merchant_logo || merchantLogo;

        const payload: ICheckoutPreviewPayload = {
            branding: {
                brand_name: customizationData?.branding?.brand_name || businessName,
                checkout_address: customizationData?.branding?.checkout_address || '',
                checkout_city: customizationData?.branding?.checkout_city || '',
                selectedCountryCode: customizationData?.branding?.selectedCountryCode || countryCode || '',
                checkout_selected_countery: customizationData?.branding?.checkout_selected_countery || countryCode || '',
                checkout_phone_number: customizationData?.branding?.checkout_phone_number || merchantPhone,
                colors: {
                    accent: customizationData?.branding?.colors?.accent || '',
                    action: customizationData?.branding?.colors?.action || '',
                },
                font: customizationData?.branding?.font || {},
                style: customizationData?.branding?.style || {},
            },
            payments: {
                methods: paymentMethods.methods,
                bnpl: paymentMethods.bnpl,
                x_pay: paymentMethods.x_pay,
                layout: customizationData?.payments?.layout || 'grid',
                pay_later: customizationData?.payments?.pay_later || false,
                toggles: {
                    display_billing: false,
                    display_shipping: false,
                    display_items: false,
                    display_savecard: customizationData?.payments?.toggles?.display_savecard || false,
                    allow_payments_retries: customizationData?.payments?.toggles?.allow_payments_retries || false,
                    enable_instant_refund: customizationData?.payments?.toggles?.enable_instant_refund || false,
                    payme_setting: paymeSettings,
                },
                show_terms: customizationData?.payments?.showTerms || false,
                terms_url: customizationData?.payments?.termsUrl ?? '',
                split_payment: {
                    enabled: customizationData?.payments?.split_payment?.enabled || false,
                    max_allowed_cards: customizationData?.payments?.split_payment?.max_allowed_cards || 3,
                },
            },
            post_payment: {
                useCustomMessage: customizationData?.post_payment?.useCustomMessage || false,
                mode: customizationData?.post_payment?.mode || 'success_page',
                auto_redirect: customizationData?.post_payment?.auto_redirect || 0,
                message: customizationData?.post_payment?.message || '',
            },
            merchant_logo: customMerchantLogo,
            isDefault: customizationData?.isDefault || false,
            activeTab: 'payments',
            previewMood,
            lang,
            order_details: {
                amount: 200000,
                currency: paymentHandleConfig.currency || countryCurrency,
                items: [],
            },
        };

        const message: ICheckoutPreviewMessage = {
            type: 'checkout-customizations',
            payload,
        };

        if (!checkoutOrigin) return;
        iframeRef.current.contentWindow.postMessage(
            message,
            checkoutOrigin
        );
    }, [paymentHandleConfig, checkoutCustomizationConfig, previewMood, countryCode, countryCurrency, lang, businessName, merchantLogo, merchantPhone, paymentMethods, checkoutOrigin]);

    useEffect(() => {
        if (open && iframeRef.current?.contentWindow && paymentHandleConfig) {
            postMessageToCheckout();
        }
    }, [open, paymentHandleConfig, checkoutCustomizationConfig, previewMood, postMessageToCheckout]);

    // Re-send when the checkout iframe announces readiness ('checkout-ready'),
    // covering the case where the initial send races ahead of the checkout's
    // listener. Validate the sender is our own preview iframe AND the checkout
    // origin before re-sending.
    useEffect(() => {
        const onCheckoutReady = (event: MessageEvent) => {
            if (event.source !== iframeRef.current?.contentWindow) return;
            if (!checkoutOrigin || event.origin !== checkoutOrigin) return;
            if (event.data?.type === 'checkout-ready') postMessageToCheckout();
        };
        window.addEventListener('message', onCheckoutReady);
        return () => window.removeEventListener('message', onCheckoutReady);
    }, [checkoutOrigin, postMessageToCheckout]);

    const renderPreview = () => (
        <div className={styles['payme-preview-modal']}>
            <div className={styles['payme-preview-modal__preview-container']}>
                <div
                    className={`${styles['payme-preview-modal__iframe-wrapper']} ${previewMood === 'mobile'
                        ? styles['payme-preview-modal__iframe-wrapper--mobile']
                        : ''
                        }`}
                >
                    <BrowserBar
                        url={getPayMeLinkUrl()}
                        isMobile={previewMood === 'mobile'}
                    />
                    <iframe
                        ref={iframeRef}
                        src={checkoutUrl}
                        title="payme_checkout_preview"
                        onLoad={postMessageToCheckout}
                        className={styles['payme-preview-modal__iframe']}
                    />
                </div>
            </div>
            <ViewModeToggle
                previewMood={previewMood}
                onModeChange={setPreviewMood}
            />
        </div>)

    return (
        <Modal
            open={open}
            onCloseHandler={onClose}
            hasCloseIcon={true}
            width="90vw"
            maxWidth="1200px"
            state="default"
            position="center"
            isOverlayAboveModal={true}
            title={t(
                'innerLayout.paymePage.handleCreationCard.previewModal.title'
            )}
            supportingText={t(
                'innerLayout.paymePage.handleCreationCard.previewModal.subtitle'
            )}
        >
            {renderPreview()}
        </Modal>
    );
};

